import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle } from "lucide-react";

const AddSenderModal = ({
  showAddForm,
  setShowAddForm,
  newSenderId,
  setNewSenderId,
  errors,
  setErrors,
  handleAddSenderId,
  isAddingSender,
}) => {
  // Restricted sender IDs from the image
  const restrictedSenderIds = [
    "MTN balance",
    "AT Money",
    "Telecel Cash",
    "MoMo",
    "MoMo balance",
    "MTN MoMo",
    "Dear Customer",
    "Y'ello",
    "Yello",
    "Blocked",
    "Block",
    "Suspended",
    "Pin",
    "your mobile money account pin code has been block",
    "your mobile money account pin code has been blocked",
    "ATM card",
    "EcoBank",
    "Cherished User",
    "GCB"
  ];

  // Multi-word restricted phrases for exact matching
  const multiWordRestricted = [
    "mtn balance",
    "at money", 
    "telecel cash",
    "momo balance",
    "mtn momo",
    "dear customer",
    "y'ello",
    "yello",
    "atm card",
    "cherished user",
    "ecobank",
    "gcb",
    "your mobile money account pin code has been block",
    "your mobile money account pin code has been blocked"
  ];

  // Single-word restricted terms (only block if they appear as complete words)
  const singleWordRestricted = [
    "blocked",
    "block",
    "suspended",
    "pin",
    "momo",
    "mtn",
    "telecel",
    "ecobank",
    "gcb",
    "yello",
    "y'ello"
  ];

  // Check if any full word/phrase from blacklist is used in the input
  const validateRestrictedSenderId = (name) => {
    const normalizedInput = name.trim().toLowerCase();

    // Don't block empty or very short inputs
    if (normalizedInput.length === 0) {
      return { isRestricted: false, restrictedWord: null, matchType: null };
    }

    // 1. Check for EXACT MATCH with any restricted sender ID
    for (const restrictedId of restrictedSenderIds) {
      if (normalizedInput === restrictedId.toLowerCase()) {
        return { 
          isRestricted: true, 
          restrictedWord: restrictedId,
          matchType: "exact"
        };
      }
    }

    // 2. Check for COMPLETE MULTI-WORD PHRASES (as whole phrases, not partials)
    for (const phrase of multiWordRestricted) {
      // Create regex with word boundaries for the entire phrase
      // This ensures "MTN MoMo" triggers but "MTN" alone doesn't
      const phraseRegex = new RegExp(`\\b${phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      
      if (phraseRegex.test(normalizedInput)) {
        return { 
          isRestricted: true, 
          restrictedWord: phrase,
          matchType: "phrase"
        };
      }
    }

    // 3. Check for COMPLETE SINGLE WORDS (only if they appear as whole words)
    const words = normalizedInput.split(/[^a-z0-9']+/).filter(word => word.length > 0);
    
    for (const word of words) {
      // Skip very short words
      if (word.length < 3) continue;
      
      // Check if this exact word is in the restricted single-word list
      for (const restrictedWord of singleWordRestricted) {
        if (word === restrictedWord.toLowerCase()) {
          return { 
            isRestricted: true, 
            restrictedWord: restrictedWord,
            matchType: "single-word"
          };
        }
      }
    }

    // 4. Special case: Check for "Y'ello" with apostrophe
    if (/y'?ello/i.test(normalizedInput) && 
        (normalizedInput.includes("y'ello") || normalizedInput.includes("yello"))) {
      // Only block if it's a complete word match
      const yelloRegex = /\b(y'?ello)\b/i;
      if (yelloRegex.test(normalizedInput)) {
        return { 
          isRestricted: true, 
          restrictedWord: "Y'ello",
          matchType: "special"
        };
      }
    }

    // No restriction found
    return { isRestricted: false, restrictedWord: null, matchType: null };
  };

  const handleClose = () => {
    setShowAddForm(false);
    setNewSenderId("");
    setErrors({});
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewSenderId(value);

    // Only allow letters, numbers, spaces, and apostrophes
    const filteredValue = value.replace(/[^A-Za-z0-9\s']/g, "");
    if (filteredValue !== value) {
      setNewSenderId(filteredValue);
    }

    // Validate for restricted IDs
    const { isRestricted, restrictedWord, matchType } = validateRestrictedSenderId(filteredValue);

    if (isRestricted) {
      let errorMessage = "";
      
      switch (matchType) {
        case "exact":
          errorMessage = `"${filteredValue}" is a restricted sender ID. Please choose a different name.`;
          break;
        case "phrase":
          errorMessage = `"${filteredValue}" contains the restricted phrase "${restrictedWord}". Please choose a different name.`;
          break;
        case "single-word":
          errorMessage = `"${filteredValue}" contains the restricted word "${restrictedWord}". Please choose a different name.`;
          break;
        case "special":
          errorMessage = `"${filteredValue}" contains the restricted term "${restrictedWord}". Please choose a different name.`;
          break;
        default:
          errorMessage = `"${filteredValue}" is a restricted sender ID. Please choose a different name.`;
      }
      
      setErrors({
        restricted: errorMessage,
      });
    } else if (errors.restricted) {
      // Clear restricted error if it's no longer triggered
      const newErrors = { ...errors };
      delete newErrors.restricted;
      setErrors(newErrors);
    } else if (errors.empty || errors.duplicate) {
      setErrors({});
    }
  };

  return (
    <AnimatePresence>
      {showAddForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Add New Sender ID
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sender ID Name
                </label>
                <input
                  type="text"
                  value={newSenderId}
                  onChange={handleInputChange}
                  placeholder="Enter sender ID (e.g., MyBrand, Shop123)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-all duration-200"
                  maxLength={11}
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-gray-500">
                    {newSenderId.length}/11 characters
                  </span>
                  {newSenderId.length > 0 && (
                    <span
                      className={`text-xs ${
                        newSenderId.length <= 11 && !errors.restricted
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {newSenderId.length <= 11 && !errors.restricted
                        ? "✓ Valid"
                        : errors.restricted ? "✗ Restricted" : "✗ Too long"}
                    </span>
                  )}
                </div>
              </div>

              {/* Guidelines Notice */}
              {/* <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-blue-800 mb-2">
                  Sender ID Guidelines:
                </h4>
                <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                  <li>Maximum 11 characters</li>
                  <li>Letters, numbers, spaces, and apostrophes only</li>
                  <li>Must contain at least one letter</li>
                  <li className="font-semibold text-red-700">
                    ⚠️ Cannot use complete restricted phrases like "MTN MoMo", "Blocked", "ATM card"
                  </li>
                  <li className="text-blue-600">
                    ✓ Partial matches like "MTN" or "Block" alone are allowed
                  </li>
                </ul>
              </div> */}

              {/* Error Messages */}
              {Object.keys(errors).length > 0 && (
                <div className="space-y-2">
                  {Object.values(errors).map((error, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-xl border border-red-200"
                    >
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{error}</span>
                    </motion.div>
                  ))}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSenderId}
                  disabled={!newSenderId.trim() || isAddingSender || Object.keys(errors).length > 0}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-gray-700 to-slate-800 text-white rounded-xl hover:from-gray-800 hover:to-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {isAddingSender ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Adding...
                    </>
                  ) : (
                    "Add Sender ID"
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddSenderModal;